import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgePercent,
  PoundSterling,
  Smartphone,
  Users,
  Package,
  ShieldCheck,
} from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import { useSiteConfig } from "@/lib/useSiteConfig";
import { AdminSignInLink } from "@/components/AdminSignInLink";

/**
 * "List your stock" retailer-start landing page (route: /sell).
 *
 * Positions FloorMatch to independent retailers with plain commercial
 * language — no SaaS jargon, no unverified 20-second claim, no publicly-quoted
 * commission percentage. Two obvious paths at the bottom:
 *   - Existing retailer → login screen with ?next=new so they land straight
 *     on the New Listing form, not the dashboard round-trip.
 *   - New retailer     → existing registration flow.
 *   - Invited retailer → prompt to use the claim link they were sent.
 */
export default function RetailerStart() {
  const { launch_phase } = useSiteConfig();
  const prelaunch = launch_phase !== "live";
  return (
    <>
      <PublicHeader />
      <main className="min-h-screen fm-grain" data-testid="retailer-start-page">
        {prelaunch && (
          <div className="bg-[#0F3D2E] text-white" data-testid="retailer-prelaunch-banner">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-start sm:items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider bg-[#E05915]/20 border border-[#E05915]/40 text-[#ffb078] px-3 py-1.5 rounded-full">
                <ShieldCheck className="w-3.5 h-3.5" /> Founding retailer phase
              </span>
              <p className="text-sm text-white/90 flex-1 min-w-[260px]">
                <span className="font-semibold">Retailer registration open — customer launch coming soon.</span>{" "}
                Sign up now to build your catalogue before we start promoting to customers, and keep
                it current as items sell in your shop. You choose when your stock is ready.
              </p>
              <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#9fe0b6] text-[#0F3D2E] px-3 py-1.5 rounded-full shrink-0" data-testid="founding-perk-badge">
                <BadgePercent className="w-3.5 h-3.5" /> Founding retailers: 0% commission
              </span>
            </div>
          </div>
        )}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-8">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-7 space-y-6">
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#2A5A3B] bg-[#2A5A3B]/10 px-3 py-1.5 rounded-full">
                For UK flooring retailers
              </span>
              <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05]">
                {prelaunch ? (
                  <>Turn dead stock into <span className="text-[#E05915]">cash</span>{" "}— get ready for launch.</>
                ) : (
                  <>Turn dead stock into <span className="text-[#E05915]">cash</span>{" "}— this month.</>
                )}
              </h1>
              <p className="text-lg text-slate-600 max-w-xl">
                {prelaunch ? (
                  <>Every roll end and pack surplus in your warehouse is money sat still. We're signing
                  up founding retailers now — list your stock so it's in front of local Manchester
                  buyers the moment we launch. You keep the margin and only pay when a sale lands.</>
                ) : (
                  <>Every roll end and pack surplus in your warehouse is money sat still. FloorMatch
                  gets it in front of local Manchester buyers actively searching for flooring that
                  fits — you keep the margin, clear the space, and only pay when a sale lands.</>
                )}
              </p>

              {/* Salesy value chips */}
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-800 text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm">
                  <PoundSterling className="w-3.5 h-3.5" /> Free to list
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-800 text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm">
                  <Smartphone className="w-3.5 h-3.5" /> List in under a minute
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-800 text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm">
                  <ShieldCheck className="w-3.5 h-3.5" /> Only pay when you sell
                </span>
                {prelaunch && (
                  <span className="inline-flex items-center gap-1.5 bg-[#0F3D2E] text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm" data-testid="founding-perk-chip">
                    <BadgePercent className="w-3.5 h-3.5" /> Founding retailers: 0% commission
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                <Link
                  to="/retailer/login?next=new"
                  className="fm-btn-primary inline-flex items-center gap-2"
                  data-testid="rs-existing-cta"
                >
                  I already have an account <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
                <Link
                  to="/retailer/register"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-slate-300 bg-white hover:bg-slate-50 font-semibold text-slate-800 text-sm"
                  data-testid="rs-new-cta"
                >
                  I'm new to FloorMatch
                </Link>
              </div>
              <p className="text-xs text-slate-500">
                Already invited by FloorMatch?{" "}
                <span className="text-slate-700 font-semibold">
                  Use the /claim link we sent you.
                </span>
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="relative">
                <div
                  className="absolute -inset-4 bg-gradient-to-br from-[#E05915]/10 to-[#2A5A3B]/10 rounded-3xl blur-2xl"
                  aria-hidden="true"
                />
                <img
                  src="https://customer-assets-lqy194kg.emergentagent.net/job_floormatch-demo/artifacts/vmzgiucw_Roll%20Ends.png"
                  alt="Stacks of surplus carpet rolls in an independent flooring warehouse"
                  loading="lazy"
                  decoding="async"
                  className="relative rounded-2xl w-full aspect-[4/5] object-cover shadow-lg"
                />
                <div className="absolute -top-3 -right-3 bg-[#2A5A3B] text-white rounded-2xl px-4 py-3 shadow-lg rotate-3">
                  <div className="text-[10px] uppercase tracking-wider font-semibold opacity-90">Typical shop</div>
                  <div className="font-display font-bold text-2xl leading-none mt-0.5">£3–8k</div>
                  <div className="text-[10px] mt-1 opacity-90">surplus stock unlocked</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-4 sm:pt-8" data-testid="retailer-benefits">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Benefit
              icon={<PoundSterling className="w-5 h-5" />}
              title="No upfront listing fees"
              body="Only pay when FloorMatch produces a sale."
            />
            <Benefit
              icon={<Smartphone className="w-5 h-5" />}
              title="Fast stock listing"
              body="Photograph and list surplus stock directly from your phone."
            />
            <Benefit
              icon={<Users className="w-5 h-5" />}
              title="Local customers"
              body="Reach people actively searching for flooring near them."
            />
            <Benefit
              icon={<Package className="w-5 h-5" />}
              title="Clear old stock"
              body="Turn roll ends, surplus packs and clearance flooring into revenue."
            />
          </div>
        </section>

        {/* How it works for retailers */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#E05915]">
            How it works for retailers
          </p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-1">
            List once. Manage from your phone.
          </h2>
          <div className="grid md:grid-cols-3 gap-4 mt-8">
            {[
              {
                n: "1",
                t: "Photograph the stock",
                d: "Snap a picture of the roll end, pack or offcut on your phone.",
              },
              {
                n: "2",
                t: "Tap in the basics",
                d: "Category, colour, dimensions and price — that's your listing live.",
              },
              {
                n: "3",
                t: "Get a paid reservation",
                d: "A local customer reserves and comes in to collect. You mark it sold.",
              },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl border border-slate-200 bg-white p-6">
                <span className="grid place-items-center w-9 h-9 rounded-lg bg-[#E05915]/10 text-[#E05915] font-display font-bold">
                  {s.n}
                </span>
                <h3 className="font-display font-semibold text-lg text-slate-900 mt-3">{s.t}</h3>
                <p className="text-sm text-slate-600 mt-1">{s.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Trust bar */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
          <div className="bg-[#2A5A3B] rounded-2xl p-8 sm:p-12 text-white grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider bg-white/10 px-3 py-1.5 rounded-full">
                <ShieldCheck className="w-3.5 h-3.5" /> Your shop stays anonymous until they pay
              </div>
              <h2 className="font-display font-bold text-3xl sm:text-4xl mt-3">
                Customer only sees your details after a paid reservation.
              </h2>
              <p className="text-white/85 mt-3 max-w-md">
                Shoppers browse your stock as a verified Greater Manchester retailer — we don't
                reveal your shop name, phone or address until the collection is confirmed.
              </p>
            </div>
            <div className="grid gap-3 text-sm">
              <TrustPoint icon={<BadgePercent className="w-4 h-4" />}>
                Commission set in Admin — no upfront cost, only a small fee on each sale
              </TrustPoint>
              <TrustPoint icon={<Package className="w-4 h-4" />}>
                Manage live, reserved and sold stock in one dashboard
              </TrustPoint>
              <TrustPoint icon={<Smartphone className="w-4 h-4" />}>
                Publish & Add Another for continuous warehouse listing sessions
              </TrustPoint>
              <TrustPoint icon={<Users className="w-4 h-4" />}>
                Every reservation comes with a verified customer name and reference code
              </TrustPoint>
            </div>
          </div>
        </section>

        {/* Final CTAs */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
          <div className="grid md:grid-cols-2 gap-4">
            <Link
              to="/retailer/login?next=new"
              className="group bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 hover:border-[#E05915]/40 hover:shadow-md transition-all"
              data-testid="rs-existing-cta-b"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-[#E05915]">
                Existing FloorMatch retailer
              </p>
              <h3 className="font-display font-bold text-2xl text-slate-900 mt-2">
                I already have an account
              </h3>
              <p className="text-slate-600 mt-2 text-sm">
                Sign in and jump straight to the new-listing form for a fast batch entry session.
              </p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#E05915] group-hover:gap-2 transition-[gap]">
                Sign in and start a listing <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
            <Link
              to="/retailer/register"
              className="group bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 hover:border-[#2A5A3B]/40 hover:shadow-md transition-all"
              data-testid="rs-new-cta-b"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-[#2A5A3B]">
                New to FloorMatch
              </p>
              <h3 className="font-display font-bold text-2xl text-slate-900 mt-2">
                I'm a new retailer
              </h3>
              <p className="text-slate-600 mt-2 text-sm">
                Create your retailer account, add your shop details and start listing surplus stock
                the same day.
              </p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#2A5A3B] group-hover:gap-2 transition-[gap]">
                Register my shop <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          </div>
        </section>

        <footer className="border-t border-slate-200 py-8">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-700 tracking-wide uppercase text-[11px]">
                FloorMatch
              </span>
              <span className="text-slate-400">·</span>
              <span className="text-[#2A5A3B] font-semibold">The perfect fit. Near you.</span>
            </div>
            <div className="flex items-center gap-4">
              <AdminSignInLink />
              <span>© 2026 FloorMatch — Made in Manchester</span>
            </div>
          </div>
        </footer>
      </main>
    </>
  );
}

function Benefit({ icon, title, body }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5">
      <div className="grid place-items-center w-10 h-10 rounded-lg bg-[#E05915]/10 text-[#E05915]">
        {icon}
      </div>
      <h3 className="font-display font-semibold text-lg text-slate-900 mt-3">{title}</h3>
      <p className="text-sm text-slate-600 mt-1">{body}</p>
    </div>
  );
}

function TrustPoint({ icon, children }) {
  return (
    <div className="flex items-start gap-2">
      <span className="grid place-items-center w-6 h-6 rounded-md bg-white/10 shrink-0 mt-0.5">
        {icon}
      </span>
      <span className="text-white/90">{children}</span>
    </div>
  );
}
